// What this browser remembers for the trader: theme, chart interval, ticket defaults. Each read
// and write survives storage that is blocked or empty.

import { createStore, useStore } from "./store";

export type Theme = "system" | "dark" | "light";

export type Prefs = {
  theme: Theme;
  interval: string;
  /** Leverage last used, per market symbol. */
  leverage: Record<string, number>;
  tapeMine: boolean;
};

const KEY = "papertrade:prefs:v1";
const DEFAULTS: Prefs = { theme: "system", interval: "5m", leverage: {}, tapeMine: false };

function read(): Prefs {
  try {
    const text = localStorage.getItem(KEY);
    return text ? { ...DEFAULTS, ...(JSON.parse(text) as Partial<Prefs>) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

export const prefs = createStore<Prefs>(read());

prefs.subscribe(() => {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs.get()));
  } catch {
    // storage blocked: kept for this tab
  }
});

export function setPref<K extends keyof Prefs>(key: K, value: Prefs[K]) {
  prefs.set((all) => ({ ...all, [key]: value }));
}

export function usePref<K extends keyof Prefs>(key: K): Prefs[K] {
  return useStore(prefs, (all) => all[key]);
}

/** Applies the theme to the document: `data-theme` when chosen, the system's otherwise. */
export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}
